import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'data/root.reducers';
import { SecurityInfo } from 'data/securityInfo/securityInfo.types';
import * as securityInfoSelectors from 'data/securityInfo/securityInfo.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as certStatusSelectors from 'data/certStatus/certStatus.selectors';
import { FieldUpdateHandlerParam } from 'shared/form';
import { createSecurityInfoBasedOnTemplate } from 'routes/SecurityInfo/templates/securityInfoTemplate.factory';
import { Button, Classes, Dialog } from '@blueprintjs/core';
import { adaptTemplateOptions, adaptTemplateForFieldUpdate } from 'routes/SecurityInfo/utils/securityInfoTemplate.adapter';
import { RetrievingDataProgress } from 'components';
import './securityInfoCreateDialog.scss';
import { CertStatus } from 'data/certStatus/certStatus.types';
import { getCertStatusPayload } from 'data/tlsProfile/tlsProfile.adapter';
import { WithAuditDetails } from 'data/types';
import { getCertStatusAuditDetailsToSubmit } from 'routes/TlsProfile/utils/certStatusDataToSubmit';
import { AuditActionType } from 'shared/userAudit/audit-constants';
import SecurityInfoForm, { SecurityInfoFormRef } from '../../components/SecurityInfoForm';
import { securityInfoTemplate } from '../../templates/securityInfoTemplate';
import getSecurityInfoDataToSubmit from '../../utils/getSecurityInfoDataToSubmit';

const mapState = (state: RootState) => ({
  isOpen: securityInfoSelectors.isSecurityInfoCreatePopupOpen(state),
  isLoading: securityInfoSelectors.isSecurityInfoLoading(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  securityInfo: securityInfoSelectors.getSecurityInfo(state),
  availableAccessFunction: supportedFeaturesSelectors.getAvailableAccessFunction(state),
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  accessFunctionTemplates: templateSelectors.getEnabledAccessFunctionFormTemplatesByKey(state),
  allCertStatuses: certStatusSelectors.getAllCertStatus(state),
});

const mapDispatch = {
  createSecurityInfo: actions.createSecurityInfoFromForm,
  closeSecurityInfoCreatePopup: actions.closeSecurityInfoCreatePopup,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type Props = PropsFromRedux;

export const SecurityInfoCreate: FunctionComponent<Props> = ({
  isOpen,
  isLoading,
  deliveryFunctionId,
  isAuditEnabled,
  userId,
  userName,
  securityInfo,
  createSecurityInfo,
  closeSecurityInfoCreatePopup,
  availableAccessFunction,
  enabledAccessFunctions,
  accessFunctionTemplates,
  allCertStatuses
}: Props) => {
  const [template, setTemplate] = useState(securityInfoTemplate);
  const [formData, setFormData] = useState<Partial<SecurityInfo>>({});
  const securityInfoFormRef = useRef<SecurityInfoFormRef>(null);

  useEffect(() => {
    setFormData({
      ...createSecurityInfoBasedOnTemplate(securityInfoTemplate),
      ...securityInfo,
    });

    let adaptedTemplate = adaptTemplateForFieldUpdate({ ...securityInfo }, securityInfoTemplate, '', '');
    if (adaptedTemplate != null) {
      adaptedTemplate = adaptTemplateOptions(adaptedTemplate, enabledAccessFunctions, accessFunctionTemplates);
      setTemplate(adaptedTemplate);
    }
  }, [isOpen, securityInfo]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    const clonedFormData = cloneDeep(formData);

    const adaptedTemplate = adaptTemplateForFieldUpdate(
      clonedFormData,
      template,
      fieldName,
      value,
    );

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
    if (adaptedTemplate != null) {
      setTemplate(adaptedTemplate);
    }
  };

  const handleClickCancel = () => closeSecurityInfoCreatePopup();

  const getCreateCertStatusPayload = (): WithAuditDetails<CertStatus> | undefined => {
    if (securityInfoFormRef.current === undefined || securityInfoFormRef.current === null) {
      return
    }

    if (!securityInfoFormRef.current.isValid(true, true)) {
      return;
    }

    const formData = securityInfoFormRef.current.getValues();
    if (formData.catype !== 'XCCA') {
      return;
    }

    const certAlreadyExists = (allCertStatuses ?? []).filter((cs) => {
      return cs.AFTYPE === (formData.aftype ?? '').substring(0, 16)
      && cs.LITYPE === formData.litype
      && cs.STATUS !== 'EXPIRED'
      && cs.STATUS !== 'REVOKED'
    }).length > 0;
    if (certAlreadyExists) {
      return;
    }

    const data = cloneDeep(getCertStatusPayload(formData));

    const payload = {
      data,
      auditDetails: getCertStatusAuditDetailsToSubmit(
        data,
        null,
        AuditActionType.CERTSTATUS_ADD,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    return payload;
  }

  const handleClickSave = () => {
    const isValid = securityInfoFormRef?.current?.isValid(true, true) ?? true;

    if (!isValid) {
      return;
    }

    const formValues = securityInfoFormRef?.current?.getValues();

    if (!formValues) {
      return;
    }

    const body = getSecurityInfoDataToSubmit(
      formValues,
      null,
      isAuditEnabled,
      userId,
      userName,
      'CREATE',
    );

    createSecurityInfo(deliveryFunctionId, body, getCreateCertStatusPayload());
  };
  const { t } = useTranslation();

  return (
    <div data-testid="SecurityInfoCreate">
      <Dialog
        isOpen={isOpen}
        title={t('securityInfo.dialog.title')}
        onClose={handleClickCancel}
        className="security-info-dialog"
      >
        <div className={Classes.DIALOG_BODY}>
          <SecurityInfoForm
            data={formData}
            template={template}
            ref={securityInfoFormRef}
            onFieldUpdate={handleFieldUpdate}
          />
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={handleClickCancel} intent="none">{t('common.button.cancel')}</Button>
            <Button onClick={handleClickSave} intent="primary" active>{t('common.button.save')}</Button>
          </div>
        </div>
      </Dialog>
      <RetrievingDataProgress isOpen={isLoading} message={t('securityInfo.dialog.saving.message')} />
    </div>
  );
};

export default connector(SecurityInfoCreate);
